import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, CheckCircle2, Notebook } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const SCALE_QUESTIONS = [
    { key: "objectifs_atteints", label: "Les objectifs pédagogiques ont-ils été atteints ?" },
    { key: "rythme_adapte", label: "Le rythme et la durée étaient-ils adaptés au groupe ?" },
    { key: "interaction_groupe", label: "Comment évaluez-vous l'interaction et l'engagement du groupe ?" },
    { key: "materiel_satisfaisant", label: "Le matériel pédagogique était-il satisfaisant ?" },
    { key: "salle_satisfaisante", label: "Les conditions d'accueil et la salle étaient-elles satisfaisantes ?" },
    { key: "note_globale", label: "Note globale que vous donnez à cette session." },
];

const TEXT_QUESTIONS = [
    { key: "deroulement", label: "Déroulement général de la session", placeholder: "Décrivez le déroulé : timing, interactivité, ajustements faits…", required: true, rows: 4 },
    { key: "points_forts", label: "Points forts de la session", placeholder: "Ce qui a particulièrement bien fonctionné.", rows: 3 },
    { key: "points_amelioration", label: "Axes d'amélioration", placeholder: "Ce qui pourrait être amélioré pour les prochaines sessions.", rows: 3 },
    { key: "observations_groupe", label: "Observations sur le groupe", placeholder: "Niveau, profils, hétérogénéité, dynamique…", rows: 3 },
    { key: "recommandations", label: "Recommandations pour Cursus Physio", placeholder: "Suggestions pour l'organisation, le matériel, la communication…", rows: 3 },
];

function ScaleInput({ value, onChange, qid }) {
    return (
        <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange(n)}
                    data-testid={`bilan-scale-${qid}-${n}`}
                    className={`h-12 flex-1 rounded-xl border text-base font-semibold transition ${
                        value === n
                            ? "border-brand-orange bg-brand-orange text-white"
                            : "border-brand-line bg-white text-brand-ink hover:border-brand-orange"
                    }`}
                >{n}</button>
            ))}
        </div>
    );
}

export default function FormateurBilan() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        deroulement: "", objectifs_atteints: 0, points_forts: "", points_amelioration: "",
        observations_groupe: "", recommandations: "", rythme_adapte: 0,
        interaction_groupe: 0, materiel_satisfaisant: 0, salle_satisfaisante: 0, note_globale: 0,
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/formateur/sessions/${id}/bilan`);
                if (data && data.submitted_at) {
                    setForm({ ...form, ...data });
                    setSubmitted(true);
                }
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        for (const q of SCALE_QUESTIONS) {
            if (!form[q.key] || form[q.key] < 1) {
                toast.error(`Merci de noter : "${q.label}"`);
                return;
            }
        }
        if (!form.deroulement || form.deroulement.trim().length < 10) {
            toast.error("Merci de décrire le déroulement (10 caractères minimum).");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/formateur/sessions/${id}/bilan`, form);
            toast.success("Bilan pédagogique enregistré");
            setSubmitted(true);
            navigate(`/formateur/sessions/${id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-brand-smoke">Chargement…</div>;
    }

    return (
        <div className="mx-auto max-w-3xl px-6 py-10" data-testid="page-formateur-bilan">
            <Link to={`/formateur/sessions/${id}`} className="inline-flex items-center gap-2 text-sm text-brand-smoke hover:text-brand-ink">
                <ArrowLeft className="h-4 w-4" /> Retour à la session
            </Link>
            <div className="mt-4 flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Qualiopi · Formateur</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Bilan pédagogique</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Document Qualiopi obligatoire à chaque session. Votre retour nourrit le suivi qualité et
                        permet d'améliorer en continu nos formations. Les réponses sont consultables par l'admin.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <Notebook className="h-7 w-7" />
                </div>
            </div>

            {submitted && (
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-1.5 text-xs font-semibold text-green-800" data-testid="bilan-submitted-banner">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Bilan déjà soumis — vous pouvez le compléter / corriger.
                </div>
            )}

            <div className="mt-8 space-y-5 rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                <h2 className="font-display text-2xl">Évaluation chiffrée (1 = très insatisfaisant · 5 = excellent)</h2>
                {SCALE_QUESTIONS.map((q) => (
                    <div key={q.key} className="rounded-xl bg-[#FAFAFA] p-4">
                        <div className="text-sm font-medium text-brand-ink">{q.label}</div>
                        <div className="mt-3">
                            <ScaleInput value={form[q.key]} onChange={(v) => set(q.key, v)} qid={q.key} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 space-y-5 rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                <h2 className="font-display text-2xl">Observations qualitatives</h2>
                {TEXT_QUESTIONS.map((q) => (
                    <div key={q.key}>
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            {q.label} {q.required && <span className="text-brand-orange">*</span>}
                        </label>
                        <textarea
                            rows={q.rows}
                            value={form[q.key] || ""}
                            onChange={(e) => set(q.key, e.target.value)}
                            placeholder={q.placeholder}
                            data-testid={`bilan-text-${q.key}`}
                            className="mt-2 w-full resize-none rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange"
                        />
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
                <Link
                    to={`/formateur/sessions/${id}`}
                    className="rounded-full border border-brand-line bg-white px-6 py-3 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                >Annuler</Link>
                <button
                    onClick={submit}
                    disabled={saving}
                    data-testid="bilan-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                >
                    <Save className="h-3.5 w-3.5" />
                    {saving ? "Enregistrement…" : submitted ? "Mettre à jour le bilan" : "Soumettre le bilan"}
                </button>
            </div>
        </div>
    );
}
